// @flow
import React from 'react';
import {useState, useEffect} from 'react';
import type {Node} from 'react';
import {
  CircularProgress,
  Box,
  Typography,
  Grid,
  makeStyles,
} from '@material-ui/core';
import type {
  FeatureUsageResponse,
  FeatureUsageConfig,
} from '../../../entities/feature-usage';
import type {StoreFeature} from '../../../entities/store-feature';
import Collapsible from '../../../elements/Collapsible/Collapsible';
import {getFeatureUsageService} from '../promises';
import {formatDate} from '../../../utils/date';
import {ProgressBar} from '../../../elements/ProgressBar/ProgressBar';

const FEATURE_LABELS: {[string]: string} = {
  TAX_INVOICE: 'Facturas electronicas',
  CHATBOT: 'Uso del chatbot',
};

type Props = {
  feature: StoreFeature,
};

const useStyles = makeStyles(theme => ({
  customCollapse: {
    marginTop: '0!important',
    backgroundColor: `#FCFCFD!important`,
    borderRadius: 8,
  },
}));

export default function UsageProgressCard({feature}: Props): Node {
  const classes = useStyles();

  const [data, setData] = useState<FeatureUsageResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const label = FEATURE_LABELS[feature] || feature;

  useEffect(() => {
    setLoading(true);
    getFeatureUsageService(feature)
      .then(response => {
        setData(response);
        setLoading(false);
      })
      .catch(() => {
        setData(null);
        setLoading(false);
      });
  }, [feature]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={2}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (!data) {
    return null;
  }

  const {totalUsed, totalLimit, configs} = data;
  const percent = totalLimit > 0 ? (totalUsed / totalLimit) * 100 : 0;

  return (
    <Collapsible
      title={
        <Grid
          container
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
        >
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1">{label}</Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="body2">
              {totalUsed}/{totalLimit}
            </Typography>
            <ProgressBar variant="determinate" value={percent} />
          </Grid>
        </Grid>
      }
      paddingV={2}
      paddingH={3}
      startClosed
      className={classes.customCollapse}
    >
      <Box display="flex" flexDirection="column" gridGap={16}>
        {configs.map((cfg: FeatureUsageConfig, idx: number) => {
          const cfgPercent =
            cfg.usageLimit > 0 ? (cfg.used / cfg.usageLimit) * 100 : 0;
          const start = formatDate(cfg.startDate, 'MMMM D, YYYY');
          const end = formatDate(cfg.endDate, 'MMMM D, YYYY');
          return (
            <Box key={idx} display="flex" flexDirection="column">
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2">{cfg.source}</Typography>
                <Typography variant="body2">
                  {cfg.used}/{cfg.usageLimit}
                </Typography>
              </Box>
              <Box mb={1}>
                <ProgressBar variant="determinate" value={cfgPercent} />
              </Box>
              <Typography variant="caption" color="textSecondary">
                {start} – {end}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Collapsible>
  );
}
